import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import { clientService } from '@services/clientService';
import CustomSelect from '../shared/CustomSelect';
import { productService } from '@services/productService';
import { FormatUtils } from '../shared/FormatUtils.jsx';

// Import all components
import ClientsList from './ClientsList';
import ClientFilters from './ClientFilters';
import ClientStats from './ClientStats';
import ClientProductsModal from './ClientProductsModal';
import { DeleteModal } from '../shared/DeleteModal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';
import { useAuth } from '@context/AuthContext';

const ClientsContent = ({ type = 'all-clients' }) => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    // State Management
    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [paginating, setPaginating] = useState(false);

    // UI State
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [selectedClientProducts, setSelectedClientProducts] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, client: null });
    const [actionLoading, setActionLoading] = useState(false);

    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [clientTypeFilter, setClientTypeFilter] = useState(
        type === 'retail-clients' ? 'RETAIL' :
            type === 'corporate-clients' ? 'CORPORATE' : 'ALL'
    );
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [consentFilter, setConsentFilter] = useState('ALL');
    const [tierFilter, setTierFilter] = useState('ALL');
    const [approvalFilter, setApprovalFilter] = useState('ALL');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // ✅ FIXED: Helper function to calculate tier from product count
    const calculateTier = (productCount) => {
        if (productCount >= 5) return 'PLATINUM';
        if (productCount >= 3 && productCount <= 4) return 'GOLD';
        if (productCount === 2) return 'SILVER';
        if (productCount === 1) return 'BRONZE';
        return 'NO_TIER';
    };

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch data when filters or pagination changes
    useEffect(() => {
        fetchClients();
    }, [currentPage, pageSize, debouncedSearchTerm, clientTypeFilter, statusFilter, consentFilter, tierFilter, approvalFilter]);

    // Fetch initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setError('');

            // Fetch products and statistics in parallel, handling each independently
            const [productsResponse, statsResponse] = await Promise.allSettled([
                productService.getAll(),
                clientService.getStatistics()
            ]);

            if (productsResponse.status === 'fulfilled' && productsResponse.value?.success && productsResponse.value?.data) {
                setProducts(productsResponse.value.data);
            }

            if (statsResponse.status === 'fulfilled' && statsResponse.value?.success && statsResponse.value?.data) {
                setStats(statsResponse.value.data);
            }
        } catch (err) {
            console.error('Error fetching initial data:', err);
        }
    };

    const fetchClients = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');

            // If there's a search term, use the search endpoint
            if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
                // Use the search endpoint
                const searchResponse = await clientService.searchClients(debouncedSearchTerm);

                if (searchResponse.success && searchResponse.data) {
                    const searchResults = searchResponse.data;

                    // Apply additional filters to search results
                    const filtered = searchResults.filter(client => {
                        const matchesType = clientTypeFilter === 'ALL' || client.clientType === clientTypeFilter;
                        const matchesStatus = statusFilter === 'ALL' ||
                            (statusFilter === 'ACTIVE' && client.active !== false) ||
                            (statusFilter === 'INACTIVE' && client.active === false);
                        const matchesConsent = consentFilter === 'ALL' ||
                            (consentFilter === 'GIVEN' && client.crossSellingConsent) ||
                            (consentFilter === 'NOT_GIVEN' && !client.crossSellingConsent);

                        let matchesTier = true;
                        if (tierFilter !== 'ALL') {
                            const productCount = client.productCount || 0;
                            const clientTier = calculateTier(productCount);

                            if (tierFilter === 'NO_TIER') {
                                matchesTier = productCount === 0;
                            } else {
                                matchesTier = clientTier === tierFilter;
                            }
                        }

                        const matchesApproval = approvalFilter === 'ALL' ||
                            client.approvalStatus === approvalFilter;

                        return matchesType && matchesStatus && matchesConsent && matchesTier && matchesApproval;
                    });

                    // Apply client-side pagination to filtered search results
                    const start = currentPage * pageSize;
                    const paginatedClients = filtered.slice(start, start + pageSize);

                    setClients(paginatedClients);
                    setTotalElements(filtered.length);
                    setTotalPages(Math.ceil(filtered.length / pageSize));
                }
            } else {
                // No search term - use paginated endpoint with filters
                const params = {
                    page: currentPage,
                    size: pageSize
                };

                // Add filters to params if they're set
                if (clientTypeFilter !== 'ALL') {
                    params.type = clientTypeFilter;
                }
                if (statusFilter !== 'ALL') {
                    params.active = statusFilter === 'ACTIVE';
                }
                if (consentFilter !== 'ALL') {
                    params.consent = consentFilter === 'GIVEN';
                }
                if (approvalFilter !== 'ALL') {
                    params.approvalStatus = approvalFilter;
                }

                const response = await clientService.getPaginated(params);

                if (response.success && response.data) {
                    const pageData = response.data;

                    if (pageData.content) {
                        let clientsData = pageData.content;
                        let totalEls = pageData.totalElements;
                        let totalPgs = pageData.totalPages;

                        // ✅ NEW: Apply tier filter to paginated results
                        if (tierFilter !== 'ALL') {
                            clientsData = clientsData.filter(client => {
                                const productCount = client.productCount || 0;
                                const clientTier = calculateTier(productCount);

                                if (tierFilter === 'NO_TIER') {
                                    return productCount === 0;
                                }

                                return clientTier === tierFilter;
                            });
                            // Recalculate pagination for filtered results
                            totalEls = clientsData.length;
                            totalPgs = Math.ceil(clientsData.length / pageSize);
                        }

                        setClients(clientsData);
                        setTotalElements(totalEls);
                        setTotalPages(totalPgs);

                        console.log('Pagination set:', {
                            totalElements: totalEls,
                            totalPages: totalPgs,
                            currentPage,
                            clients: clientsData.length
                        });
                    } else {
                        // Fallback for non-paginated response
                        let clientsData = Array.isArray(pageData) ? pageData : [];

                        // ✅ NEW: Apply tier filter to fallback data
                        if (tierFilter !== 'ALL') {
                            clientsData = clientsData.filter(client => {
                                const productCount = client.productCount || 0;
                                const clientTier = calculateTier(productCount);

                                if (tierFilter === 'NO_TIER') {
                                    return productCount === 0;
                                }

                                return clientTier === tierFilter;
                            });
                        }

                        setClients(clientsData);
                        setTotalElements(clientsData.length);
                        setTotalPages(Math.ceil(clientsData.length / pageSize));

                        console.log('Fallback pagination set:', {
                            totalElements: clientsData.length,
                            totalPages: Math.ceil(clientsData.length / pageSize),
                            currentPage
                        });
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching clients:', err);
            setError('Failed to load clients. Please try again.');
            setClients([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setPaginating(false);
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, clientTypeFilter, statusFilter, consentFilter, tierFilter, approvalFilter]);

    // Delete handler
    const handleDeleteClient = async () => {
        if (!deleteModal.client) return;

        try {
            setActionLoading(true);
            const response = await clientService.delete(deleteModal.client.id);

            if (response.success) {
                await fetchClients();
                await fetchInitialData(); // Refresh stats
                setDeleteModal({ open: false, client: null });
            } else {
                throw new Error(response.message || 'Failed to delete client');
            }
        } catch (err) {
            console.error('Error deleting client:', err);
            setError(err.message || 'Failed to delete client');
        } finally {
            setActionLoading(false);
        }
    };

    // Navigation handlers
    const handleAddClient = () => {
        navigate('/clients/new');
    };

    const handleViewClient = (client) => {
        navigate(`/clients/${client.id}`);
    };

    const handleEditClient = (client) => {
        // Navigate to edit page using ClientFormPage
        navigate(`/clients/${client.id}/edit`);
    };

    const handleViewProducts = (client) => {
        setSelectedClientProducts(client);
        setShowProductsModal(true);
    };

    // Pagination Component
    const Pagination = () => {
        const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
        const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

        return (
            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                        <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                        <span className="font-medium">{totalElements.toLocaleString()}</span> results
                    </span>

                    <CustomSelect
                        value={pageSize}
                        onChange={(val) => {
                            setPageSize(Number(val));
                            setCurrentPage(0);
                        }}
                        options={[
                            { value: 10, label: '10 per page' },
                            { value: 20, label: '20 per page' },
                            { value: 50, label: '50 per page' },
                            { value: 100, label: '100 per page' }
                        ]}
                        dropUp
                    />
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrentPage(0)}
                        disabled={currentPage === 0 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="First page"
                    >
                        <ChevronsLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 0 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Previous page"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <span className="px-4 py-2 text-sm text-gray-700">
                        Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                    </span>

                    <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Next page"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Last page"
                    >
                        <ChevronsRight className="w-5 h-5" />
                    </button>

                    {paginating && (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />
                    )}
                </div>
            </div>
        );
    };

    // Show loading state for initial load only
    if (loading && clients.length === 0) {
        return <LoadingSpinner message="Loading clients..." />;
    }

    // Main clients list view
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        {type === 'retail-clients' ? 'Retail Clients' :
                            type === 'corporate-clients' ? 'Corporate Clients' :
                                'All Clients'}
                    </h2>
                    <p className="text-gray-600 mt-1">
                        {totalElements.toLocaleString()} total clients
                    </p>
                </div>
                {hasPermission('CLIENTS:CREATE') && (
                    <button
                        onClick={handleAddClient}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add Client
                    </button>
                )}
            </div>

            {/* Error Alert */}
            {error && <ErrorAlert error={error} onRetry={fetchClients} />}

            {/* Client Stats */}
            {stats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Total Clients</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {(stats.totalClients || 0).toLocaleString()}
                        </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Retail</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {(stats.retailClients || 0).toLocaleString()}
                        </p>
                        {stats.retailPercentage && (
                            <p className="text-xs text-gray-400 mt-1">
                                {FormatUtils.formatPercentage(stats.retailPercentage, 0)}
                            </p>
                        )}
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Corporate</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {(stats.corporateClients || 0).toLocaleString()}
                        </p>
                        {stats.corporatePercentage && (
                            <p className="text-xs text-gray-400 mt-1">
                                {FormatUtils.formatPercentage(stats.corporatePercentage, 0)}
                            </p>
                        )}
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">With Products</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {(stats.clientsWithProducts || 0).toLocaleString()}
                        </p>
                        {stats.averageProductsPerClient && (
                            <p className="text-xs text-gray-400 mt-1">
                                Avg: {Number(stats.averageProductsPerClient).toFixed(1)}
                            </p>
                        )}
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Prospects</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {(stats.prospects || 0).toLocaleString()}
                        </p>
                        {stats.clientsWithConsent !== undefined && (
                            <p className="text-xs text-gray-400 mt-1">
                                Consent: {(stats.clientsWithConsent || 0).toLocaleString()}
                            </p>
                        )}
                    </div>
                </div>
            ) : (
                <ClientStats clients={clients} products={products} />
            )}

            {/* Filters */}
            <ClientFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                clientTypeFilter={clientTypeFilter}
                onClientTypeChange={setClientTypeFilter}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                consentFilter={consentFilter}
                onConsentChange={setConsentFilter}
                tierFilter={tierFilter}
                onTierChange={setTierFilter}
                approvalFilter={approvalFilter}
                onApprovalChange={setApprovalFilter}
            />

            {/* Clients List with Pagination */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                <ClientsList
                    clients={clients}
                    onView={handleViewClient}
                    onEdit={handleEditClient}
                    onDelete={(client) => setDeleteModal({ open: true, client })}
                    onViewProducts={handleViewProducts}
                    hasPermission={hasPermission}
                />

                {/* Pagination Controls - Show if more than 1 page OR if there are results */}
                {(totalPages > 0 || totalElements > 0) && <Pagination />}
            </div>

            {/* Client Products Modal */}
            <ClientProductsModal
                isOpen={showProductsModal}
                onClose={() => {
                    setShowProductsModal(false);
                    setSelectedClientProducts(null);
                }}
                client={selectedClientProducts}
                products={products}
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, client: null })}
                onConfirm={handleDeleteClient}
                itemName={deleteModal.client ?
                    FormatUtils.formatName(deleteModal.client.clientType === 'CORPORATE'
                        ? { firstName: deleteModal.client.companyName }
                        : deleteModal.client)
                    : ''}
                itemType="client"
                loading={actionLoading}
            />
        </div>
    );
};

export default ClientsContent;
